import {
  CreateInvitationMutation,
  CreateInvitationMutationVariables,
  DashInviteRoleEnum,
} from "@/__generated__/graphql";
import { CREATE_INVITATION_MUTATION } from "@/api/mutations/createInvitation";
import { InputWrapper } from "@/components/FormInputs/InputWrapper";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { useOrg } from "@/hooks/useOrg";
import { useRoute } from "@/routes/getRoute";
import { MutationErrorHandler } from "@/util/mutationUtils";
import { useMutation } from "@apollo/client";
import React, { useEffect } from "react";
import { Control, useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { formatIncompletePhoneNumber } from "libphonenumber-js";

interface InviteFields {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  role: DashInviteRoleEnum;
}

export function InviteForm() {
  const { t } = useTranslation("translation", {
    keyPrefix: "teamManagement.inviteForm.details",
  });
  const { org } = useOrg();
  const navigate = useNavigate();
  const getRoute = useRoute();

  const {
    register,
    handleSubmit,
    control,
    formState: { isValid, isSubmitting, errors },
  } = useForm<InviteFields>({ defaultValues: { phoneNumber: "+1" } });

  const [createInvitation, { loading, data, error }] = useMutation<
    CreateInvitationMutation,
    CreateInvitationMutationVariables
  >(CREATE_INVITATION_MUTATION);

  const indexPath = getRoute("teammates");

  useEffect(() => {
    if (data) {
      const errorHandler = MutationErrorHandler.fromResult(
        { data },
        error?.graphQLErrors,
      );
      if (errorHandler.isSuccess()) {
        if (data.createDashInviteMutation?.messages?.length) {
          data.createDashInviteMutation?.messages?.forEach(
            ({ title, message }) => {
              toast.success(title, { description: message });
            },
          );
        } else {
          toast.success(t("submitSuccess.title"), {
            description: t("submitSuccess.description"),
          });
        }
        navigate(indexPath);
      } else {
        toast.error(t("submitError.title"), {
          description:
            errorHandler
              .getErrors()
              ?.map(({ message }) => message)
              .join(", ") || t("submitError.description"),
        });
      }
    }
  }, [data, error?.graphQLErrors, indexPath, navigate, t]);

  const onSubmit = (values: InviteFields) => {
    const { phoneNumber, ...rest } = values;
    if (org?.id) {
      createInvitation({
        variables: {
          organizationId: org?.id,
          phoneNumber: phoneNumber.replace(/\s/g, ""),
          ...rest,
        },
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card className="max-w-190">
        <CardHeader className="p-6">
          <CardTitle>{t("title")}</CardTitle>
          <CardDescription>{t("description")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col">
          <div className="flex max-w-130 flex-col gap-6 p-2">
            <InputWrapper
              name="firstName"
              label={t("firstName")}
              error={errors.firstName}
            >
              <Input
                id="firstName"
                {...register("firstName", { required: true })}
                type="text"
              />
            </InputWrapper>
            <InputWrapper
              name="lastName"
              label={t("lastName")}
              error={errors.lastName}
            >
              <Input
                id="lastName"
                {...register("lastName", { required: true })}
                type="text"
              />
            </InputWrapper>
            <InputWrapper name="email" label={t("email")} error={errors.email}>
              <Input
                id="email"
                {...register("email", { required: true })}
                type="email"
              />
            </InputWrapper>
            <PhoneField control={control} />
            <RoleSelect control={control} />
          </div>
        </CardContent>
        <CardFooter className="justify-end border-t pt-6">
          <Button disabled={loading || !isValid || isSubmitting} type="submit">
            {t("sendInvitation")}
          </Button>
        </CardFooter>
      </Card>
    </form>
  );
}

function RoleSelect({ control }: { control: Control<InviteFields> }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "teamManagement.inviteForm.details",
  });
  const {
    field,
    fieldState: { error },
  } = useController({ name: "role", control, rules: { required: true } });

  return (
    <InputWrapper name="role" label={t("role")} error={error}>
      <Select onValueChange={field.onChange} defaultValue={field.value}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t("rolePlaceholder")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={DashInviteRoleEnum.Counselor}>
            {t("counselor")}
          </SelectItem>
          <SelectItem value={DashInviteRoleEnum.Influencer}>
            {t("influencer")}
          </SelectItem>
        </SelectContent>
      </Select>
    </InputWrapper>
  );
}

function PhoneField({ control }: { control: Control<InviteFields> }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "teamManagement.inviteForm.details",
  });
  const {
    field,
    fieldState: { error },
  } = useController({
    name: "phoneNumber",
    control,
    rules: { required: true },
  });

  return (
    <InputWrapper name="phoneNumber" label={t("phoneNumber")} error={error}>
      <Input
        id="phoneNumber"
        type="tel"
        minLength={12}
        value={formatIncompletePhoneNumber(field.value)}
        onBlur={field.onBlur}
        ref={field.ref}
        name={field.name}
        onChange={({ target: { value } }) => {
          field.onChange(
            !value.length || value.startsWith("+") ? value : `+${value}`,
          );
        }}
      />
    </InputWrapper>
  );
}
